import { useCallback, useMemo } from 'react';

import { LOCAL_STORAGE_KEY, MATCHING_DEFAULT } from '@common/constants';
import { useQueryStringParameters } from '@common/hooks/useQueryStringParameters/useQueryStringParameters';
import {
    ApplianceSectionType,
    ApplianceUrls,
    RetailerFileSettings,
    RetailerSettings,
    UserOption,
} from '@common/models';
import { isDevMode } from '@common/utils';
import {
    RetailerFileSettingsParams,
    useFetchRetailerFileSettingsQuery,
} from '@ee-api/retailer-settings-api/retailerSettingsFileService';
import {
    RetailerSettingsParams,
    useFetchRetailerSettingsQuery,
} from '@ee-api/retailer-settings-api/retailerSettingsService';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';

import { AxiosBaseQueryFunction } from '../../api/axiosBaseQuery';

// update UserOption for new options
export type UserOptionReturn = {
    [key in UserOption]: boolean;
};

type useRetailerSettingsReturn = {
    retailerSettings: RetailerSettings | undefined;
    retailerFileSettings: RetailerFileSettings | undefined;
    isFetchingRetailerSettings: boolean;
    isFetchingRetailerFileSettings: boolean;
    getInStoreApplianceUrl: (applianceType: ApplianceSectionType) => string | undefined;
    // other results data from Settings API request
    retailerSettingsQueryResults: Omit<
        TypedUseQueryHookResult<RetailerSettings, RetailerSettingsParams, AxiosBaseQueryFunction>,
        'data' | 'isFetching'
    >;
    // other query result data from Settings File Request
    retailerFileSettingsQueryResults: Omit<
        TypedUseQueryHookResult<RetailerFileSettings, RetailerFileSettingsParams, AxiosBaseQueryFunction>,
        'data' | 'isFetching'
    >;
    userOptions: UserOptionReturn;
};

const emptyOptions = {} as UserOptionReturn;

/**
 * Deep merge utility function to merge retailer-specific settings with default settings
 * Arrays are replaced (not merged), objects are deeply merged, primitives are overridden
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any, sonarjs/cognitive-complexity
const deepMerge = <T extends Record<string, any>>(target: T, source: Partial<T>): T => {
    const result = { ...target };

    for (const key in source) {
        // eslint-disable-next-line no-prototype-builtins
        if (source.hasOwnProperty(key)) {
            const sourceValue = source[key];
            const targetValue = result[key];

            if (sourceValue === null || sourceValue === undefined) {
                // Keep the target value if source is null/undefined
                continue;
            }

            if (Array.isArray(sourceValue)) {
                // Replace arrays entirely
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                result[key] = sourceValue as any;
            } else if (typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
                // Deep merge objects
                // eslint-disable-next-line max-depth
                if (typeof targetValue === 'object' && !Array.isArray(targetValue) && targetValue !== null) {
                    result[key] = deepMerge(targetValue, sourceValue);
                } else {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    result[key] = sourceValue as any;
                }
            } else {
                // Override primitives
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                result[key] = sourceValue as any;
            }
        }
    }

    return result;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useRetailerSettings = (): useRetailerSettingsReturn => {
    const { userToken } = useQueryStringParameters();

    // this one fetches the settings from the API
    const {
        isFetching: isFetchingRetailerSettings,
        data: _retailerSettings,
        ...retailerSettingsQueryResults
    } = useFetchRetailerSettingsQuery({ params: userToken ? { userToken: userToken } : undefined });

    /**
     * insert default enableMatching as false, override with retailer settings
     * if we keep this, we'll want it to come from the database, not local
     */
    const retailerSettings: RetailerSettings = useMemo(() => {
        return {
            enableMatching: Number(sessionStorage.getItem(LOCAL_STORAGE_KEY.ENABLE_MATCHING) ?? MATCHING_DEFAULT),
            ..._retailerSettings,
        } as RetailerSettings;
    }, [_retailerSettings]);

    const makeRetailFileUrl = (code?: string) => (code ? `/public/assets/retailers/${code}.settings.json` : '');
    const companyCodeOrDevelopment = isDevMode() ? 'development' : retailerSettings?.companyCode;

    // Fetch default settings
    const { isFetching: isFetchingDefaultSettings, data: defaultFileSettings } = useFetchRetailerFileSettingsQuery({
        url: '/public/assets/retailers/default.settings.json',
    });

    // Fetch retailer-specific settings
    const {
        isFetching: isFetchingRetailerSpecificSettings,
        data: retailerSpecificSettings,
        ...retailerFileSettingsQueryResults
    } = useFetchRetailerFileSettingsQuery(
        {
            url: makeRetailFileUrl(companyCodeOrDevelopment),
        },
        {
            skip: !companyCodeOrDevelopment,
        }
    );

    // Merged result from default settings and retailer-specific settings overrides
    const retailerFileSettings = useMemo(() => {
        if (!defaultFileSettings) {
            return retailerSpecificSettings;
        }
        if (!retailerSpecificSettings) {
            return defaultFileSettings;
        }
        return deepMerge(defaultFileSettings, retailerSpecificSettings);
    }, [defaultFileSettings, retailerSpecificSettings]);

    // Combined fetching state
    const isFetchingRetailerFileSettings = isFetchingDefaultSettings || isFetchingRetailerSpecificSettings;

    const getInStoreApplianceUrl = useCallback(
        (applianceType: ApplianceSectionType): string | undefined => {
            const getInStoreApplianceUrlsFromRetailerSettings = (
                settings: RetailerFileSettings | undefined
            ): ApplianceUrls | undefined => {
                return settings ? settings.inStoreApplianceURLs : undefined;
            };

            const inStoreApplianceURLs = getInStoreApplianceUrlsFromRetailerSettings(retailerFileSettings);
            return inStoreApplianceURLs ? inStoreApplianceURLs[applianceType] : undefined;
        },
        [retailerFileSettings]
    );

    /**
     * userOptions may only include what's referenced in UserOption enum
     */
    const userOptions: UserOptionReturn = useMemo(() => {
        return Object.values(UserOption).reduce((result, option) => {
            return { ...result, [option]: retailerSettings?.userOptions?.includes(option) ?? false };
        }, emptyOptions);
    }, [retailerSettings?.userOptions]);

    return {
        retailerSettings,
        retailerFileSettings,
        isFetchingRetailerSettings,
        isFetchingRetailerFileSettings,
        retailerSettingsQueryResults,
        retailerFileSettingsQueryResults,
        getInStoreApplianceUrl,
        userOptions,
    };
};
